import { TFunction, useTranslation } from 'react-i18next';
import Edit from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import { DataGridPremiumProps, GridRenderCellParams, GridColDef, GridRowId } from '@mui/x-data-grid-premium';
import { CboRole } from '@cbo/shared-library';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import { Theme, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DataGrid, { BaseDataGridProps } from '../../components/DataGrid/DataGrid';
import {
  LaborRuleApplicabilityData,
  LaborRuleConfigData,
  LaborRuleGroup,
  ModalType,
  statesWithGrouping,
  WagesRuleTypes,
} from '../models/LaborRuleConfiguration';
import { enrichSiteRows } from '../utils/utils';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import BulkActionDataGrid from '../../components/BulkActionDataGrid/BulkActionDataGrid';
import { DynamicButtonInfo } from '../../models/DynamicButtonInfo';
import { dataGridFocussedRowCellHoverSx } from '../../components/BulkActionDataGrid/styles';

export interface JurisdictionSitesRow {
  [x: string]: unknown;
  id: string;
  hierarchy: string[];
}

interface JurisdictionSitesDataGridProps extends Partial<BaseDataGridProps> {
  sites: JurisdictionSitesRow[];
  getRowHeight?: () => number;
  jurisdictionPath: string;
  ruleGroup: LaborRuleGroup;
  allRules?: LaborRuleConfigData[];
  editModeVariant?: boolean;
  onEditRule?: (stateOverrides?: Record<string, unknown>) => void;
  ruleType?: string;
  onCalculationClick?: (mode: ModalType, selectedRows: GridRowId[]) => void;
  ruleApplicabilities?: LaborRuleApplicabilityData[] | undefined;
}

const createColumn = (
  field: string,
  headerName: string,
  renderCell?: (params: GridRenderCellParams) => JSX.Element
) => ({
  field,
  headerName,
  flex: 1,
  minWidth: 120,
  ...(renderCell ? { renderCell } : {}),
});
const defaultColumns = ['siteName', 'referenceId', 'configuration', 'effectiveDate', 'effectiveUntil', 'changeReason'];
const editModeColumns = ['state', 'siteName', 'referenceId', 'configuration'];
const ruleTypeColumnsMap: Record<string, { columns: string[]; disableEditButton?: boolean }> = {
  [WagesRuleTypes.TipMakeupPayRule]: {
    columns: ['referenceId', 'siteName', 'isApplicable', 'effectiveDate'],
    disableEditButton: true,
  },
};

export const getColumnsWithRuleData = (ruleConfigs: LaborRuleConfigData[], t: TFunction<'translation', undefined>) => {
  const columnsFromRule = ruleConfigs.length
    ? Object.keys(ruleConfigs[0].rule)
        .filter((key) => !['type', 'group'].includes(key))
        .map((key) => createColumn(key, t(`laborRules.columnHeaders.${key}`)))
    : [];
  const leftColumns = defaultColumns
    .slice(0, 3)
    .map((name) => createColumn(name, t(`laborRules.columnHeaders.${name}`)));
  const rightColumns = defaultColumns
    .slice(3, 6)
    .map((name) => createColumn(name, t(`laborRules.columnHeaders.${name}`)));
  return [...leftColumns, columnsFromRule[0], ...rightColumns];
};

export const getColumns = (
  t: TFunction<'translation', undefined>,
  editModeVariant: boolean,
  jurisdictionPath: string,
  isUserLREWriter: boolean,
  onEditRule?: (stateOverrides?: Record<string, unknown>) => void,
  ruleType?: string,
  theme?: Theme
) => {
  let columnNames: string[] = [];
  let disableEditButton = false;

  if (ruleType && ruleTypeColumnsMap[ruleType]) {
    columnNames = ruleTypeColumnsMap[ruleType].columns;
    disableEditButton = ruleTypeColumnsMap[ruleType].disableEditButton ?? false;
  } else {
    columnNames = editModeVariant ? editModeColumns : defaultColumns;
  }

  const columns = columnNames.map((name) => {
    if (name === 'isApplicable') {
      return createColumn(name, t(`laborRules.columnHeaders.${name}`), (params) =>
        params.row[name] === true ? (
          <CheckIcon sx={{ color: theme?.palette.success.main }} />
        ) : (
          <ClearIcon sx={{ color: theme?.palette.error.main }} />
        )
      );
    }
    return createColumn(name, t(`laborRules.columnHeaders.${name}`));
  });
  if (editModeVariant) return columns;

  return [
    ...columns,
    ...(disableEditButton
      ? []
      : [
          {
            ...createColumn('action', t(`laborRules.columnHeaders.action`)),
            renderCell: (params: GridRenderCellParams) => {
              const groupName = params.id.toString().split('/').at(-1);
              const jurisdictionName = jurisdictionPath.split('.').at(-1)?.replaceAll('_', ' ');
              const localityPath =
                groupName === jurisdictionName ? '' : `${jurisdictionPath}.${groupName?.replaceAll(' ', '_')}`;
              return (
                params.row.action || (
                  <Tooltip title={t('laborRules.tooltips.editLocality')}>
                    <Button disabled={isUserLREWriter} onClick={() => onEditRule && onEditRule({ localityPath })}>
                      <Edit sx={{ m: 'auto' }} />
                    </Button>
                  </Tooltip>
                )
              );
            },
          },
        ]),
  ];
};

export const getEnableGroupingSettings = (jurisdictionPath: string, ruleGroup: LaborRuleGroup) => {
  const getTreeDataPath: DataGridPremiumProps['getTreeDataPath'] = (row) => row.hierarchy;
  const groupingColDef: DataGridPremiumProps['groupingColDef'] = {
    headerName: 'Locality',
  };
  const CARuleGroupWithGrouping = [LaborRuleGroup.SCHEDULING, LaborRuleGroup.WAGES_AND_TIPS];
  return jurisdictionPath === 'United_States_of_America.California'
    ? (CARuleGroupWithGrouping.includes(ruleGroup) && { treeData: true, groupingColDef }) || {}
    : (statesWithGrouping.includes(jurisdictionPath) &&
        ruleGroup === LaborRuleGroup.SCHEDULING && { treeData: true, getTreeDataPath, groupingColDef }) ||
        {};
};

function JurisdictionSitesDataGrid(props: JurisdictionSitesDataGridProps) {
  const theme = useTheme();
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const {
    sites,
    getRowHeight,
    jurisdictionPath,
    ruleGroup,
    editModeVariant,
    onEditRule,
    allRules,
    ruleType,
    ruleApplicabilities,
    onCalculationClick,
  } = props;
  const { t } = useTranslation();
  const user = useUsers();
  const isUserLREWriter = !isPermitted(user, [CboRole.LRE_RULE_WRITER]);
  const columns = getColumns(t, !!editModeVariant, jurisdictionPath, isUserLREWriter, onEditRule, ruleType, theme);
  const rows = enrichSiteRows(
    sites,
    allRules,
    jurisdictionPath,
    t,
    isUserLREWriter,
    onEditRule,
    ruleType === WagesRuleTypes.TipMakeupPayRule ? ruleApplicabilities : null
  );
  const enableGrouping = editModeVariant ? {} : getEnableGroupingSettings(jurisdictionPath, ruleGroup);
  const bulkActionButtons: DynamicButtonInfo[] = [
    {
      dataTestId: 'bulk-activate-button',
      text: t('buttonText.enableCalculation'),
      icon: <CheckCircleIcon />,
      functionality(selection) {
        if (onCalculationClick) {
          onCalculationClick(ModalType.TurnOnCalculation, selection);
        }
      },
    },
    {
      dataTestId: 'bulk-deactivate-button',
      text: t('buttonText.disableCalculation'),
      icon: <CancelIcon />,
      functionality(selection) {
        if (onCalculationClick) {
          onCalculationClick(ModalType.TurnOffCalculation, selection);
        }
      },
    },
  ];

  return ruleType === WagesRuleTypes.TipMakeupPayRule ? (
    <BulkActionDataGrid
      rows={rows}
      columns={columns}
      buttons={bulkActionButtons}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={(newSelection) => setSelectionModel([...newSelection])}
      checkboxSelection={!isUserLREWriter}
      customResetActions={() => setSelectionModel([])}
      disableRowSelectionOnClick
      sx={dataGridFocussedRowCellHoverSx(theme)}
    />
  ) : (
    <DataGrid
      {...props}
      {...enableGrouping}
      columns={columns as GridColDef[]}
      rows={rows}
      disableColumnSelector={false}
      itemsPerPage={5}
      sx={{ mt: '1rem' }}
      dataTestId='jurisdiction-sites-table'
      getRowHeight={getRowHeight}
    />
  );
}

JurisdictionSitesDataGrid.defaultProps = {
  getRowHeight: undefined,
  editModeVariant: false,
  onEditRule: undefined,
  allRules: undefined,
  ruleType: undefined,
  onCalculationClick: undefined,
  ruleApplicabilities: undefined,
};

export default JurisdictionSitesDataGrid;
